// src/hooks/useTimeSliderState.jsx
import { useState, useMemo, useEffect } from "react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { getNearestFutureTick } from "@/utils/vesselHelper"

dayjs.extend(utc)

/**
 * Kapselt die komplette TimeSlider-Logik:
 * - Berechnet sliderTicks aus fromPicker/toPicker
 * - Initialisiert und verwaltet sliderPosition
 * - Leitet selectedTimestamp als kanonischen ISO-UTC-String ab
 */
export function useTimeSliderState({ fromPicker, toPicker }) {
  const [sliderPosition, setSliderPosition] = useState({ value: [], label: [] })

  const stepHours = 6 // Schrittweite wie vorher im VesselController

  const sliderTicks = useMemo(() => {
    const ticks = []
    let sliderTickCounter = 0
    let currentTime = fromPicker

    while (currentTime.isBefore(toPicker) || currentTime.isSame(toPicker)) {
      ticks.push({
        value: sliderTickCounter,
        label: currentTime.format("YYYY-MM-DDTHH:mm:ss") + ".000Z",
      })
      currentTime = currentTime.add(stepHours, "hour")
      sliderTickCounter += stepHours
    }

    // Finalen Tick am Ende immer hinzufügen (wie vorher)
    ticks.push({
      value: sliderTickCounter,
      label: currentTime.format("YYYY-MM-DDTHH:mm:ss") + ".000Z",
    })

    return ticks
  }, [fromPicker, toPicker])

  // First build snaps to the nearest future tick; rebuilds keep the previous
  // slider time mapped onto the new grid.
  useEffect(() => {
    if (sliderTicks.length === 0) return
    setSliderPosition((prev) => {
      const prevMs =
        typeof prev?.label === "string" ? dayjs.utc(prev.label).valueOf() : NaN
      if (!Number.isFinite(prevMs)) {
        const lastTick = sliderTicks[sliderTicks.length - 1]
        return getNearestFutureTick(sliderTicks, lastTick) ?? prev
      }
      return sliderTicks.reduce((best, curr) =>
        Math.abs(dayjs.utc(curr.label).valueOf() - prevMs) <
        Math.abs(dayjs.utc(best.label).valueOf() - prevMs)
          ? curr
          : best,
      )
    })
  }, [sliderTicks])

  const selectedTimestamp = useMemo(() => {
    if (
      typeof sliderPosition?.label !== "string" ||
      sliderPosition.label.length === 0
    ) {
      return null
    }
    return sliderPosition.label.replace(/\+00:00$/, ".000Z")
  }, [sliderPosition])

  return { sliderTicks, sliderPosition, setSliderPosition, selectedTimestamp }
}
